<template>
  <div class="mapa-comunidad">
    <!-- Grid: mapa (60%) + info panel (40%) -->
    <div class="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6">
      <!-- ============ MAPA ============ -->
      <div class="lg:col-span-3 col-span-1">
        <div class="relative">
          <div
            ref="mapContainer"
            class="h-[min(70svh,560px)] min-h-[440px] w-full overflow-hidden rounded-xl border border-white/50 bg-bochas-bg sm:h-[560px] lg:h-[500px] 2xl:h-[650px] lg:min-h-0"
            aria-label="Mapa interactivo de asociaciones de bochas en Córdoba"
          ></div>
        </div>
      </div>

      <!-- ============ INFO PANEL ============ -->
      <aside
        ref="infoPanel"
        class="hidden lg:flex lg:col-span-2 card-fbochas p-4 sm:p-5 min-h-[280px] sm:min-h-[320px] lg:min-h-[500px] flex-col lg:sticky lg:top-4"
      >
        <!-- Empty state -->
        <div
          v-if="!selected"
          class="flex-1 flex flex-col items-center justify-center text-center py-6"
        >
          <div
            class="w-16 h-16 sm:w-12 sm:h-12 rounded-lg bg-bochas-rojo flex items-center justify-center mb-4"
          >
            <IconFbochas name="location" :size="32" color="white" />
          </div>
          <h3 class="text-2xl sm:text-xl font-semibold text-white mb-8">
            Seleccioná una asociación
          </h3>
          <p class="text-lg font-normal text-bochas-text-muted">
            Explorá el mapa interactivo de Córdoba y hacé click sobre una sede
            para ver más información.
          </p>
        </div>

        <!-- Selected state -->
        <div v-else class="flex-1 flex flex-col">
          <div class="flex flex-1 flex-col">
            <div class="aspect-[16/9] overflow-hidden rounded-2xl bg-bochas-bg">
              <img
                v-if="selected.imagen"
                :src="selected.imagen"
                :alt="selected.nombre"
                class="w-full h-full object-cover"
                loading="lazy"
              />
              <div v-else class="w-full h-full bg-degradado-bochas"></div>
            </div>

            <h3 class="mt-4 text-xl font-bold leading-tight text-white">
              {{ selected.nombre }}
            </h3>

            <p
              v-if="selected.localidad"
              class="mt-4 inline-flex w-fit self-start items-center gap-2 rounded-full bg-[#1D527C] px-3 py-1.5 text-sm font-medium text-white"
            >
              <IconFbochas name="location" :size="16" color="white" />
              {{ selected.localidad }}
            </p>

            <div
              class="mt-5 grid grid-cols-1 gap-2 lg:gap-4 border-t border-white/10 pt-5 text-sm text-white sm:grid-cols-2"
            >
              <div class="space-y-2">
                <p v-if="selected.fundacion" class="flex items-center gap-2">
                  <IconFbochas name="calendar" :size="18" color="#a52121" />
                  Fundacion {{ selected.fundacion }}
                </p>
                <p class="flex items-center gap-2">
                  <IconFbochas name="users" :size="18" color="#a52121" />
                  {{ selected.cantidadClubes || 0 }} Clubes afiliados
                </p>
              </div>

              <div
                v-if="selected.teléfono || selected.dirección || selected.email"
                class="space-y-2"
              >
                <p v-if="selected.teléfono" class="flex items-center gap-2">
                  <IconFbochas name="phone" :size="18" color="#a52121" />
                  {{ selected.teléfono }}
                </p>
                <p v-if="selected.dirección" class="flex items-center gap-2">
                  <IconFbochas name="location" :size="18" color="#a52121" />
                  {{ selected.dirección }}
                </p>
                <p v-if="selected.email" class="flex items-center gap-2">
                  <IconFbochas name="mail" :size="18" color="#a52121" />
                  {{ selected.email }}
                </p>
              </div>
            </div>

            <RichTextContent
              v-if="selected.descripción"
              :html="selected.descripción"
              class="mt-5 text-sm leading-relaxed text-gray-100"
            />

            <div
              v-if="selected.clubesDestacados?.length"
              class="mt-6 hidden flex-1"
            >
              <p class="mb-3 text-base font-medium leading-tight text-white">
                Conoce nuestros Clubes afiliados
              </p>
              <ul class="space-y-2.5">
                <li
                  v-for="club in selected.clubesDestacados"
                  :key="club.id || club.nombre"
                >
                  <button
                    type="button"
                    class="flex w-full items-center justify-between gap-3 rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2.5 text-left transition-colors hover:bg-white/[0.08]"
                    @click="selectedClub = club"
                  >
                    <div class="min-w-0">
                      <p class="truncate text-sm font-medium text-white">
                        {{ club.nombre }}
                      </p>
                      <p class="mt-0.5 truncate text-xs text-bochas-text-muted">
                        {{ club.localidad
                        }}<span v-if="club.jugadores">
                          - {{ club.jugadores }} jugadores</span
                        >
                      </p>
                    </div>
                    <IconFbochas
                      name="arrow-right"
                      :size="20"
                      class="flex-shrink-0 text-white"
                    />
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <!-- ============ MODAL DEL CLUB ============ -->
    <Teleport to="body">
      <Transition name="bottom-sheet-modal">
        <div
          v-if="selectedClub"
          class="mobile-modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 backdrop-blur-md sm:items-center sm:p-5"
          @click.self="dismissSelectedClub"
        >
          <div
            v-bottom-sheet="closeSelectedClub"
            class="mobile-bottom-sheet mapa-club-sheet vue-managed-sheet flex h-auto w-full max-h-[88svh] flex-col overflow-y-auto overscroll-contain rounded-t-3xl border border-white/10 bg-[#171d2d] text-white shadow-2xl sm:max-h-[88vh] sm:max-w-2xl sm:rounded-3xl lg:max-h-[90vh] lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl"
            @click.stop
          >
            <!-- Cover -->
            <div
              class="relative h-36 flex-shrink-0 overflow-hidden border-b-2 border-b-[#1D527C] bg-bochas-bg sm:h-48 lg:h-64 2xl:h-72"
            >
              <img
                v-if="selectedClub.imagen"
                :src="selectedClub.imagen"
                :alt="selectedClub.nombre"
                class="w-full h-full object-cover"
              />
              <div v-else class="w-full h-full bg-degradado-bochas"></div>
              <div
                class="absolute inset-0 bg-gradient-to-r from-black/65 via-black/25 to-black/10"
              ></div>
              <button
                @click="dismissSelectedClub"
                class="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/20 text-xl text-white backdrop-blur transition-colors hover:bg-white/30 sm:top-4 sm:right-4"
                aria-label="Cerrar"
              >
                ✕
              </button>
              <div
                class="absolute left-5 top-1/2 z-10 -translate-y-1/2 pr-14 sm:left-7 lg:left-10"
              >
                <h3
                  class="text-xl font-bold leading-tight text-white sm:text-2xl lg:text-3xl 2xl:text-4xl"
                >
                  {{ selectedClub.nombre }}
                </h3>
              </div>
            </div>

            <!-- Body -->
            <div class="flex flex-col p-5 sm:p-6 lg:p-8 2xl:p-10">
              <div
                class="flex flex-col gap-2 text-sm sm:flex-row sm:flex-wrap sm:gap-x-6 sm:gap-y-3 sm:text-base"
              >
                <span
                  v-if="selectedClub.localidad"
                  class="flex items-center gap-2 text-white"
                >
                  <IconFbochas name="location" :size="20" color="#a52121" />
                  {{ selectedClub.localidad }}
                </span>
                <span
                  v-if="selectedClub.fundacion"
                  class="flex items-center gap-2 text-white"
                >
                  <IconFbochas name="calendar" :size="20" color="#a52121" />
                  Fundación {{ selectedClub.fundacion }}
                </span>
                <span
                  v-if="selectedClub.jugadores"
                  class="flex items-center gap-2 text-white"
                >
                  <IconFbochas name="users" :size="20" color="#a52121" />
                  {{ selectedClub.jugadores }} asociados
                </span>
              </div>

              <RichTextContent
                v-if="selectedClub.descripción"
                :html="selectedClub.descripción"
                class="mt-6 max-w-5xl text-[0.95rem] leading-relaxed text-gray-100 sm:text-base lg:text-lg"
              />

              <div v-if="selectedClub.galería?.length" class="mt-7 space-y-3">
                <p class="text-base font-bold text-white lg:text-lg">
                  Galería de fotos
                </p>
                <div class="flex gap-4 overflow-x-auto pb-3 scrollbar-hide">
                  <img
                    v-for="(img, idx) in selectedClub.galería"
                    :key="idx"
                    :src="img"
                    :alt="`${selectedClub.nombre} ${idx + 1}`"
                    class="h-32 w-52 flex-shrink-0 cursor-pointer rounded-xl border border-white/10 object-cover transition-transform hover:scale-[1.03] sm:h-32 sm:w-52 lg:h-40 lg:w-64 2xl:h-44 2xl:w-72"
                    loading="lazy"
                  />
                </div>
              </div>

              <div
                v-if="
                  selectedClub.teléfono ||
                  selectedClub.dirección ||
                  selectedClub.email
                "
                class="mt-8 flex flex-col gap-2 border-t border-white/10 pt-5 text-sm text-gray-200 sm:mt-10 sm:flex-row sm:flex-wrap sm:gap-x-7 sm:gap-y-3 lg:mt-12 lg:text-base"
              >
                <a
                  v-if="selectedClub.teléfono"
                  :href="`tel:${selectedClub.teléfono.replace(/[^+\d]/g, '')}`"
                  class="flex items-center gap-2 transition-colors hover:text-white"
                >
                  <IconFbochas name="phone" :size="19" color="#a52121" />
                  {{ selectedClub.teléfono }}
                </a>
                <span
                  v-if="selectedClub.dirección"
                  class="flex items-center gap-2"
                >
                  <IconFbochas name="location" :size="19" color="#a52121" />
                  {{ selectedClub.dirección }}
                </span>
                <a
                  v-if="selectedClub.email"
                  :href="`mailto:${selectedClub.email}`"
                  class="flex items-center gap-2 transition-colors hover:text-white"
                >
                  <IconFbochas name="mail" :size="19" color="#a52121" />
                  {{ selectedClub.email }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- ============ MODAL DE ASOCIACIÓN (mobile) ============ -->
    <Teleport to="body">
      <Transition name="bottom-sheet-modal">
        <div
          v-if="showSelectedModal && selected"
          class="mobile-modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 backdrop-blur-md sm:items-center sm:p-5"
          @click.self="closeSelectedModal"
        >
          <div
            v-bottom-sheet="closeSelectedModal"
            class="mobile-bottom-sheet mapa-asoc-sheet vue-managed-sheet flex h-auto w-full min-h-[60vh] max-h-[88svh] flex-col overflow-y-auto overscroll-contain rounded-t-3xl border border-white/10 bg-[#171d2d] text-white shadow-2xl sm:min-h-[60vh] sm:max-h-[88vh] sm:max-w-2xl sm:rounded-3xl"
            @click.stop
          >
            <!-- Cover -->
            <div
              class="relative h-50 flex-shrink-0 overflow-hidden border-b-2 border-b-[#1D527C] bg-bochas-bg sm:h-48"
            >
              <img
                v-if="selected.imagen"
                :src="selected.imagen"
                :alt="selected.nombre"
                class="w-full h-full object-cover"
              />
              <div v-else class="w-full h-full bg-degradado-bochas"></div>
              <div
                class="absolute inset-0 bg-gradient-to-r from-black/65 via-black/25 to-black/10"
              ></div>
              <button
                @click="closeSelectedModal"
                class="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/20 text-xl text-white backdrop-blur transition-colors hover:bg-white/30 sm:top-4 sm:right-4"
                aria-label="Cerrar"
              >
                ✕
              </button>
              <div
                class="absolute left-5 top-1/2 z-10 -translate-y-1/2 pr-14 sm:left-7"
              >
                <h3
                  class="text-xl font-bold leading-tight text-white sm:text-2xl"
                >
                  {{ selected.nombre }}
                </h3>
              </div>
            </div>

            <!-- Body -->
            <div class="flex flex-col p-5 sm:p-6">
              <p
                v-if="selected.localidad"
                class="inline-flex w-fit self-start items-center gap-2 rounded-full bg-[#1D527C] px-3 py-1.5 text-sm font-medium text-white"
              >
                <IconFbochas name="location" :size="16" color="white" />
                {{ selected.localidad }}
              </p>

              <div
                class="mt-5 grid grid-cols-1 gap-2 border-t border-white/10 pt-5 text-sm text-white sm:grid-cols-2"
              >
                <div class="space-y-2">
                  <p v-if="selected.fundacion" class="flex items-center gap-2">
                    <IconFbochas name="calendar" :size="18" color="#a52121" />
                    Fundacion {{ selected.fundacion }}
                  </p>
                  <p class="flex items-center gap-2">
                    <IconFbochas name="users" :size="18" color="#a52121" />
                    {{ selected.cantidadClubes || 0 }} Clubes afiliados
                  </p>
                </div>

                <div
                  v-if="
                    selected.teléfono || selected.dirección || selected.email
                  "
                  class="space-y-2"
                >
                  <p v-if="selected.teléfono" class="flex items-center gap-2">
                    <IconFbochas name="phone" :size="18" color="#a52121" />
                    {{ selected.teléfono }}
                  </p>
                  <p v-if="selected.dirección" class="flex items-center gap-2">
                    <IconFbochas name="location" :size="18" color="#a52121" />
                    {{ selected.dirección }}
                  </p>
                  <p v-if="selected.email" class="flex items-center gap-2">
                    <IconFbochas name="mail" :size="18" color="#a52121" />
                    {{ selected.email }}
                  </p>
                </div>
              </div>

              <RichTextContent
                v-if="selected.descripción"
                :html="selected.descripción"
                class="mt-5 text-sm leading-relaxed text-gray-100"
              />
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import RichTextContent from "@/components/Common/RichTextContent.vue";
import { listarAsociaciones } from "@/services/asociaciones/asociacionesService";
import { listarClubes } from "@/services/clubes/clubesService";
import { obtenerSiteSettingsMap } from "@/services/siteSettings/siteSettingsService";
import { closeBottomSheet } from "@/directives/bottomSheet";

const mapContainer = ref(null);
const infoPanel = ref(null);
let map = null;
let geoLayer = null;
let markersLayer = null;
let mapDisposed = false;
let zoomHandler = null;
let resizeHandler = null;
const selected = ref(null);
const selectedClub = ref(null);
const showSelectedModal = ref(false);

function closeSelectedClub() {
  selectedClub.value = null;
}

function dismissSelectedClub() {
  closeBottomSheet(closeSelectedClub);
}

function selectAssociation(item) {
  selected.value = item;
  if (isMobileMapViewport()) {
    showSelectedModal.value = true;
    document.body.style.overflow = "hidden";
  }
}

function closeSelectedModal() {
  showSelectedModal.value = false;
  document.body.style.overflow = "";
}

function isMobileMapViewport() {
  return window.matchMedia("(max-width: 639px)").matches;
}

const asociaciones = ref([]);

function normalizeCityName(name) {
  return String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function isImportantCity(name) {
  const importantCities = new Set([
    "cordoba",
    "cordoba capital",
    "rio cuarto",
    "villa maria",
    "san francisco",
    "bell ville",
    "cruz del eje",
    "alta gracia",
  ]);
  return importantCities.has(normalizeCityName(name));
}

function getMarkerLevel(clubs, localidad) {
  if (isImportantCity(localidad) && clubs >= 5) return "xl";
  if (isImportantCity(localidad)) return "lg";
  if (clubs >= 20) return "xl";
  if (clubs >= 12) return "lg";
  if (clubs >= 6) return "md";
  return "sm";
}

function getMarkerSizes(clubs, localidad) {
  const level = getMarkerLevel(clubs, localidad);
  const sizes = {
    sm: { size: 22 },
    md: { size: 28 },
    lg: { size: 36 },
    xl: { size: 44 },
  };
  return { level, ...sizes[level] };
}

function getMarkerKey(item) {
  const key = normalizeCityName(item.localidad || item.name);
  if (key === "cordoba capital") return "cordoba";
  return key;
}

function getMarkerScore(item) {
  const clubs = item.cantidadClubes ?? item.clubs ?? 0;
  return clubs + (isImportantCity(item.localidad || item.name) ? 6 : 0);
}

function getFeaturedMarkerKeys() {
  return new Set(
    asociaciones.value
      .filter((item) => getMarkerKey(item))
      .sort((a, b) => getMarkerScore(b) - getMarkerScore(a))
      .slice(0, 6)
      .map(getMarkerKey),
  );
}

function toNumberCoord(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizeGallery(gallery) {
  if (Array.isArray(gallery)) return gallery;
  if (!gallery || typeof gallery !== "string") return [];
  try {
    const parsed = JSON.parse(gallery);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
}

function mapClub(club) {
  return {
    id: club.id,
    nombre: club.name || club.nombre || "Club sin nombre",
    localidad: club.locality || club.localidad || "",
    fundacion: club.foundationYear || club.fundacion || "",
    jugadores: club.playerCount ?? club.jugadores ?? 0,
    cancha: club.courtType || club.cancha || "",
    dirección: club.address || club.dirección || "",
    teléfono: club.phone || club.teléfono || "",
    email: club.email || "",
    descripción: club.description || club.descripción || "",
    imagen: club.heroImageUrl || club.logoUrl || club.imagen || "",
    galería: normalizeGallery(club.gallery || club.galería),
  };
}

function mapAsociacion(federación, clubes) {
  const lat = toNumberCoord(federación.lat);
  const lng = toNumberCoord(federación.lng);
  if (lat === null || lng === null) return null;

  return {
    id: federación.id,
    nombre: federación.name || federación.nombre || "Federación sin nombre",
    localidad: federación.locality || federación.localidad || "",
    región: federación.región || "",
    fundacion: federación.foundationYear || federación.fundacion || "",
    descripción: federación.description || federación.descripción || "",
    dirección: federación.address || federación.dirección || "",
    teléfono: federación.phone || federación.teléfono || "",
    email: federación.email || "",
    cantidadJugadores:
      federación.playerCount ?? federación.cantidadJugadores ?? 0,
    cantidadClubes: federación.clubCount ?? clubes.length,
    coords: [lat, lng],
    imagen:
      federación.heroImageUrl || federación.imageUrl || federación.imagen || "",
    clubesDestacados: clubes.map(mapClub),
  };
}

async function loadCommunityData() {
  try {
    const [asociacionesRes, settings] = await Promise.all([
      listarAsociaciones(),
      obtenerSiteSettingsMap(),
    ]);
    const provinces = JSON.parse(settings["instituciones.provincias"] || "[]");
    const cordobaIds = new Set(provinces
      .filter((province) => String(province.name || "").trim().normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "").toLowerCase() === "cordoba")
      .map((province) => String(province.id)));
    if (!Array.isArray(asociacionesRes?.items)) {
      throw new Error("Respuesta invalida de /asociaciones");
    }
    let clubesRes = { items: [] };
    try {
      clubesRes = await listarClubes();
    } catch (err) {
      console.warn("No se pudieron cargar clubes del backend:", err);
    }
    const asociacionesItems = asociacionesRes.items || [];
    const clubes = Array.isArray(clubesRes.items) ? clubesRes.items : [];
    const clubesByAsociacion = clubes.reduce((acc, club) => {
      const asociacionId = club.asociacionId || club.federationId;
      if (!asociacionId) return acc;
      if (!acc.has(asociacionId)) acc.set(asociacionId, []);
      acc.get(asociacionId).push(club);
      return acc;
    }, new Map());
    const mapped = asociacionesItems
      .filter((federación) => !federación.provinceId || cordobaIds.has(String(federación.provinceId)))
      .map((federación) =>
        mapAsociacion(federación, clubesByAsociacion.get(federación.id) || []),
      )
      .filter(Boolean);

    asociaciones.value = mapped;
  } catch (err) {
    asociaciones.value = [];
    console.warn("No se pudieron cargar asociaciones del backend:", {
      message: err.message,
      status: err.response?.status,
      baseURL: err.config?.baseURL,
      url: err.config?.url,
    });
  }
}

function createMarkerCardHtml(asoc, clubs, showDot = true) {
  return `
    <div class="mapa-marker-combo">
      <div class="mapa-pin-card">
        <div class="mapa-pin-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#a52121" stroke="#a52121" stroke-width="0">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z"/>
          </svg>
        </div>
        <div class="mapa-pin-body">
          <strong>${asoc.localidad}</strong>
          <small>${clubs} clubes</small>
        </div>
      </div>
      ${
        showDot
          ? `<div class="mapa-pin-dot">
        <div class="mapa-pin-dot-inner"></div>
      </div>`
          : ""
      }
    </div>
  `;
}

const createBigMarker = (asoc, featured = false) => {
  const clubs = asoc.cantidadClubes || 0;
  const markerSize = getMarkerSizes(clubs, asoc.localidad);
  const size = markerSize.size;
  const lng = asoc.coords?.[1];
  const tooltipDirection = lng > -62.7 ? "left" : lng < -64.9 ? "right" : "top";

  const marker = L.circleMarker(asoc.coords, {
    radius: Math.max(7, Math.round(size / 2)),
    color: "#ffffff",
    weight: 2,
    fillColor: featured ? "#b92525" : "#a52121",
    fillOpacity: 1,
    opacity: 1,
    interactive: true,
  });

  marker.bindTooltip(createMarkerCardHtml(asoc, clubs, false), {
    direction: tooltipDirection,
    offset:
      tooltipDirection === "top"
        ? [0, -Math.round(size / 2)]
        : [
            tooltipDirection === "left"
              ? -Math.round(size / 2)
              : Math.round(size / 2),
            0,
          ],
    opacity: 1,
    pane: "markerTooltipPane",
    className: "mapa-marker-card-tooltip",
    permanent: featured,
    interactive: true,
  });
  marker._mapaFeatured = featured;

  return marker;
};

function updateMarkerTooltipsByZoom() {
  if (mapDisposed || !map || !map._mapPane || !markersLayer) return;
  const zoom = map.getZoom();
  markersLayer.eachLayer((marker) => {
    const tooltip = marker.getTooltip();
    if (!tooltip) return;
    if (marker._mapaFeatured || zoom >= 7.5) {
      marker.openTooltip();
    } else {
      marker.closeTooltip();
    }
  });
}

function renderAssociationMarkers() {
  if (mapDisposed || !map) return;
  if (markersLayer) {
    markersLayer.clearLayers();
  } else {
    markersLayer = L.layerGroup().addTo(map);
  }

  const featuredMarkerKeys = getFeaturedMarkerKeys();
  asociaciones.value.forEach((item) => {
    const marker = createBigMarker(
      item,
      featuredMarkerKeys.has(getMarkerKey(item)),
    );
    marker.on("click", () => {
      selectAssociation(item);
    });
    marker.on("tooltipopen", (event) => {
      const element = event.tooltip?.getElement();
      if (!element) return;
      element.onclick = () => selectAssociation(item);
    });
    markersLayer.addLayer(marker);
  });
  updateMarkerTooltipsByZoom();
}

onMounted(async () => {
  if (!mapContainer.value) return;
  mapDisposed = false;

  const bounds = [
    [-35.7, -66.7],
    [-29.0, -61.0],
  ];

  map = L.map(mapContainer.value, {
    zoomControl: false,
    attributionControl: false,
    scrollWheelZoom: true,
    doubleClickZoom: true,
    touchZoom: true,
    dragging: true,
    zoomAnimation: true,
    fadeAnimation: true,
    markerZoomAnimation: false,
    zoomSnap: 0.25,
    zoomDelta: 0.25,
    wheelPxPerZoomLevel: 120,
    inertia: true,
    inertiaDeceleration: 3000,
    minZoom: 6,
    maxZoom: 10,
    maxBounds: bounds,
    maxBoundsViscosity: 1,
  }).setView([-31.4, -64.2], 6);

  L.control.zoom({ position: "topright" }).addTo(map);
  map.createPane("geoPane");
  map.createPane("markerTooltipPane");
  map.getPane("geoPane").style.zIndex = 200;
  map.getPane("markerPane").style.zIndex = 650;
  map.getPane("markerTooltipPane").style.zIndex = 1000;

  await loadCommunityData();
  if (mapDisposed || !map) return;

  try {
    const res = await fetch("/geo/cordoba.json");
    const data = await res.json();
    if (mapDisposed || !map) return;

    geoLayer = L.geoJSON(data, {
      pane: "geoPane",
      interactive: false,
      style: {
        fillColor: "#1d527c",
        fillOpacity: 0.5,
        color: "#ffffff",
        weight: 1.5,
        opacity: 0.7,
      },
    }).addTo(map);

    const geoBounds = geoLayer.getBounds();
    map.setMaxBounds(geoBounds.pad(0.12));
    map.fitBounds(geoBounds, {
      padding: isMobileMapViewport() ? [24, 24] : [4, 4],
      animate: false,
    });
    map.setZoom(
      Math.min(
        map.getZoom() + (isMobileMapViewport() ? 0 : 0.5),
        map.getMaxZoom(),
      ),
      { animate: false },
    );
  } catch (err) {
    console.warn("No se pudo cargar /geo/cordoba.json:", err);
  }

  renderAssociationMarkers();
  zoomHandler = () => updateMarkerTooltipsByZoom();
  resizeHandler = () => {
    if (mapDisposed || !map || !geoLayer) return;
    map.fitBounds(geoLayer.getBounds(), {
      padding: isMobileMapViewport() ? [24, 24] : [4, 4],
      animate: false,
    });
  };
  map.on("zoomend", zoomHandler);
  map.on("resize", resizeHandler);
});
onBeforeUnmount(() => {
  mapDisposed = true;
  document.body.style.overflow = "";
  if (map) {
    const mapToRemove = map;
    if (zoomHandler) {
      mapToRemove.off("zoomend", zoomHandler);
      zoomHandler = null;
    }
    if (resizeHandler) {
      mapToRemove.off("resize", resizeHandler);
      resizeHandler = null;
    }
    mapToRemove.stop();

    // Leaflet conserva un timeout al animar el zoom. Evitamos que se ejecute
    // después de que remove() haya eliminado el mapPane.
    mapToRemove._animatingZoom = false;

    if (geoLayer) {
      geoLayer.remove();
      geoLayer = null;
    }
    if (markersLayer) {
      markersLayer.remove();
      markersLayer = null;
    }
    mapToRemove.remove();
    map = null;
  }
});
</script>

<style scoped>
/* Marker combo: card blanco arriba + punto rojo abajo */
:deep(.mapa-custom-marker) {
  background: transparent !important;
  border: none !important;
}
:deep(.mapa-marker-combo) {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: max-content;
  min-width: 150px;
  max-width: 220px;
  cursor: pointer;
  font-family: "Poppins", system-ui, sans-serif;
  transform-origin: bottom center;
  animation: mapa-marker-pop 0.28s ease-out both;
  transition: transform 0.16s ease;
}
:deep(.mapa-marker-combo:hover) {
  transform: translateY(-3px);
}
:deep(.mapa-pin-card) {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(0, 0, 0, 0.05);
  white-space: normal;
}
:deep(.mapa-pin-icon) {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgba(165, 33, 33, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
:deep(.mapa-pin-body) {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  min-width: 0;
}
:deep(.mapa-pin-body strong) {
  color: #1a1f2e;
  font-size: 13px;
  font-weight: 600;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow-wrap: anywhere;
}
:deep(.mapa-pin-body small) {
  color: rgba(0, 0, 0, 0.55);
  font-size: 11px;
}
:deep(.mapa-pin-dot) {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(165, 33, 33, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
}
:deep(.mapa-pin-dot-inner) {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #a52121;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  animation: mapa-dot-pulse 1.8s ease-out infinite;
}
:deep(.mapa-marker-dot) {
  width: var(--marker-size, 28px);
  height: var(--marker-size, 28px);
  border-radius: 999px;
  background: #a52121;
  border: 2px solid #fff;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.4), 0 0 0 4px rgba(165, 33, 33, 0.16);
  animation: mapa-marker-pop 0.28s ease-out both,
    mapa-dot-glow 2s ease-out infinite;
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}
:deep(.mapa-marker-dot:hover) {
  transform: scale(1.14);
  box-shadow: 0 7px 18px rgba(0, 0, 0, 0.48), 0 0 0 6px rgba(165, 33, 33, 0.22);
}
:deep(.mapa-marker-lg),
:deep(.mapa-marker-xl) {
  background: #b92525;
}
:deep(.mapa-marker-card-tooltip) {
  border: 0;
  background: transparent !important;
  box-shadow: none;
  padding: 0;
  z-index: 1000 !important;
}

@keyframes mapa-marker-pop {
  from {
    opacity: 0;
    transform: scale(0.72);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes mapa-dot-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(165, 33, 33, 0.42);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(165, 33, 33, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(165, 33, 33, 0);
  }
}

@keyframes mapa-dot-glow {
  0%,
  100% {
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.4), 0 0 0 4px rgba(165, 33, 33, 0.16);
  }
  50% {
    box-shadow: 0 5px 14px rgba(0, 0, 0, 0.46),
      0 0 0 7px rgba(165, 33, 33, 0.08);
  }
}

/* Punto rojo simple para ciudades sin asociación */
</style>

<style>
/* Animación del modal */
.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: 0.28s ease;
}
.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
  transform: scale(0.96);
}

/* Ocultar scrollbar de la galería */
.scrollbar-hide {
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.mobile-bottom-sheet.mapa-club-sheet::before {
  display: none;
}

.mobile-bottom-sheet.mapa-asoc-sheet::before {
  display: none;
}

@media (max-width: 639px) {
  .mobile-bottom-sheet.mapa-club-sheet,
  .mobile-bottom-sheet.mapa-asoc-sheet {
    min-height: 70vh;
    max-height: 88svh;
    border-radius: 1.5rem 1.5rem 0 0;
  }
}
</style>
